import jsonTitans from '../constants/titans.json';
import jsonKratosTables from '../constants/kratos-tables.json';
import jsonWeapons from '../constants/weapons.json';
import jsonGear from '../constants/gear.json';
import jsonMnemos from '../constants/mnemos.json';
import {
    ARMORY_STORAGE_MODEL_VERSION,
    BUNDLED_ARMORY_SHARE_CONTRACT_VERSION,
    CURRENT_SCHEMA_VERSION,
    LocalStorageKeys,
    SpoilerTag,
} from '../constants/constants';
import {
    ArmoryItem,
    ArmoryItemSource,
    ArmoryLoadout,
    Argonaut,
    Gear,
    KratosTable,
    Mnemos,
    StoredArmoryLoadout,
    Titan,
    Weapon,
} from '../types';
import type { SharedArmoryItemSelection, SharedArmoryRecipe } from './loadout-sharing';
import { deepCopy } from './data-replication';
import { getBundledATCCSnapshot } from './atcc-snapshot';
import { mergeATCCItems, normalizeATCCItem } from './atcc-merge';
import { sanitizeArmoryLoadout } from './sanitization';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type ArmoryCollectionKey = 'titans' | 'kratosTables' | 'weapons' | 'gear' | 'mnemos';

export interface EffectiveArmoryData {
    titans: Titan[];
    kratosTables: KratosTable[];
    weapons: Weapon[];
    gear: Gear[];
    mnemos: Mnemos[];
}

export interface StoredArmoryOverrides {
    titans?: Titan[];
    kratosTables?: KratosTable[];
    weapons?: Weapon[];
    gear?: Gear[];
    mnemos?: Mnemos[];
}

interface LegacyStoredArmoryData {
    titans: Titan[];
    kratosTables: KratosTable[];
    weapons: Weapon[];
    gear: Gear[];
    mnemos: Mnemos[];
}

export interface ArmoryMigrationResult {
    migrated: boolean;
    needsResetWarning: boolean;
    message: string;
}

export interface SharedArmoryImportPreview {
    armory: EffectiveArmoryData;
    loadout: ArmoryLoadout;
    requiredTags: string[];
}

const BUNDLED_ATCC_SNAPSHOT = getBundledATCCSnapshot();

const BUNDLED_ARMORY: EffectiveArmoryData = {
    titans: attachBundledMetadata(
        mergeATCCItems(
            annotateBundledSourceItems(jsonTitans as unknown as Titan[], 'titans', 'base'),
            annotateBundledSourceItems(BUNDLED_ATCC_SNAPSHOT.titans as Titan[], 'titans', 'atcc'),
        ) as Titan[],
        'titans',
    ),
    kratosTables: attachBundledMetadata(
        mergeATCCItems(
            annotateBundledSourceItems(jsonKratosTables as unknown as KratosTable[], 'kratosTables', 'base'),
            annotateBundledSourceItems(BUNDLED_ATCC_SNAPSHOT.kratosTables as KratosTable[], 'kratosTables', 'atcc'),
        ) as KratosTable[],
        'kratosTables',
    ),
    weapons: attachBundledMetadata(
        mergeATCCItems(
            annotateBundledSourceItems(jsonWeapons as unknown as Weapon[], 'weapons', 'base'),
            annotateBundledSourceItems(BUNDLED_ATCC_SNAPSHOT.weapons as Weapon[], 'weapons', 'atcc'),
        ) as Weapon[],
        'weapons',
    ),
    gear: attachBundledMetadata(
        mergeATCCItems(
            annotateBundledSourceItems(jsonGear as unknown as Gear[], 'gear', 'base'),
            annotateBundledSourceItems(BUNDLED_ATCC_SNAPSHOT.gear as Gear[], 'gear', 'atcc'),
        ) as Gear[],
        'gear',
    ),
    mnemos: attachBundledMetadata(annotateBundledSourceItems(jsonMnemos as unknown as Mnemos[], 'mnemos', 'base'), 'mnemos'),
};

export function getBundledArmoryData(): EffectiveArmoryData {
    return deepCopy(BUNDLED_ARMORY);
}


export function getEffectiveArmoryData(overrides?: StoredArmoryOverrides | null): EffectiveArmoryData {
    const bundled = getBundledArmoryData();

    return {
        titans: mergeBundledAndCustomItems('titans', bundled.titans, overrides?.titans ?? []),
        kratosTables: mergeBundledAndCustomItems('kratosTables', bundled.kratosTables, overrides?.kratosTables ?? []),
        weapons: mergeBundledAndCustomItems('weapons', bundled.weapons, overrides?.weapons ?? []),
        gear: mergeBundledAndCustomItems('gear', bundled.gear, overrides?.gear ?? []),
        mnemos: mergeBundledAndCustomItems('mnemos', bundled.mnemos, overrides?.mnemos ?? []),
    };
}

export function loadArmoryOverrides(storage: StorageLike = localStorage): StoredArmoryOverrides | null {
    const parsed = parseStoredJson<StoredArmoryOverrides>(storage.getItem(LocalStorageKeys.ARMORY_OVERRIDES));
    if (!parsed || typeof parsed !== 'object') {
        return null;
    }

    return {
        titans: normalizeStoredCustomCollection('titans', parsed.titans),
        kratosTables: normalizeStoredCustomCollection('kratosTables', parsed.kratosTables),
        weapons: normalizeStoredCustomCollection('weapons', parsed.weapons),
        gear: normalizeStoredCustomCollection('gear', parsed.gear),
        mnemos: normalizeStoredCustomCollection('mnemos', parsed.mnemos),
    };
}

export function loadEffectiveArmoryFromStorage(storage: StorageLike = localStorage): EffectiveArmoryData {
    const overrides = loadArmoryOverrides(storage);
    if (overrides) {
        return getEffectiveArmoryData(overrides);
    }

    return getEffectiveArmoryData(deriveLegacyOverrides(storage).overrides);
}

export function saveArmoryOverrides(armory: EffectiveArmoryData, storage: StorageLike = localStorage) {
    const overrides = deriveOverridesFromEffectiveArmory(armory);
    const hasCustomItems = Object.values(overrides).some((items) => (items ?? []).length > 0);

    if (hasCustomItems) {
        storage.setItem(LocalStorageKeys.ARMORY_OVERRIDES, JSON.stringify(overrides));
    } else {
        storage.removeItem(LocalStorageKeys.ARMORY_OVERRIDES);
    }

    storage.setItem(LocalStorageKeys.ARMORY_STORAGE_MODEL, ARMORY_STORAGE_MODEL_VERSION);
}

export function clearStoredArmoryData(storage: StorageLike = localStorage) {
    storage.removeItem(LocalStorageKeys.ARMORY_OVERRIDES);
    storage.removeItem(LocalStorageKeys.TITANS);
    storage.removeItem(LocalStorageKeys.KRATOS_TABLES);
    storage.removeItem(LocalStorageKeys.WEAPONS);
    storage.removeItem(LocalStorageKeys.GEAR);
    storage.removeItem(LocalStorageKeys.MNEMOS);
    storage.removeItem(LocalStorageKeys.LOADOUTS);
}

export function createDefaultArmoryLoadout(armory: EffectiveArmoryData): ArmoryLoadout {
    return sanitizeArmoryLoadout({
        name: '',
        argonaut: {
            name: 'Argonaut',
            rage: 0,
            fate: 0,
            danger: 0,
        } as Argonaut,
        titan: armory.titans.find((item) => item.name === 'Dreamwalker') || armory.titans[0],
        kratosTable: armory.kratosTables.find((item) => item.name === 'Dreamwalker') || armory.kratosTables[0],
        weapon: armory.weapons.find((item) => item.name === 'Fists') || armory.weapons[0],
        gear: [],
        mnemos: [],
    });
}

export function loadStoredArmoryLoadouts(
    armory: EffectiveArmoryData,
    storage: StorageLike = localStorage,
): ArmoryLoadout[] {
    const defaultLoadout = createDefaultArmoryLoadout(armory);
    const storedLoadouts = parseStoredJson<Array<StoredArmoryLoadout | ArmoryLoadout>>(storage.getItem(LocalStorageKeys.LOADOUTS));

    if (!Array.isArray(storedLoadouts) || storedLoadouts.length === 0) {
        return [defaultLoadout];
    }

    try {
        if (isStoredLoadoutReferenceArray(storedLoadouts)) {
            const resolved = storedLoadouts
                .map((loadout) => resolveStoredArmoryLoadout(loadout, armory, defaultLoadout))
                .filter(Boolean) as ArmoryLoadout[];

            return resolved.length > 0 ? resolved : [defaultLoadout];
        }

        const legacyResolved = rebindArmoryLoadouts(
            storedLoadouts.map((loadout) => sanitizeArmoryLoadout(loadout)),
            armory,
            defaultLoadout,
        );

        return legacyResolved.length > 0 ? legacyResolved : [defaultLoadout];
    } catch (error) {
        console.error(`Error loading armory loadouts from storage - ${(error as Error).message}`);
        return [defaultLoadout];
    }
}

export function saveStoredArmoryLoadouts(
    loadouts: ArmoryLoadout[],
    armory: EffectiveArmoryData,
    storage: StorageLike = localStorage,
) {
    const defaultLoadout = createDefaultArmoryLoadout(armory);
    const serialized = loadouts.map((loadout) => serializeArmoryLoadout(loadout, armory, defaultLoadout));
    storage.setItem(LocalStorageKeys.LOADOUTS, JSON.stringify(serialized));
}

export function rebindArmoryLoadouts(
    loadouts: ArmoryLoadout[],
    armory: EffectiveArmoryData,
    defaultLoadout: ArmoryLoadout = createDefaultArmoryLoadout(armory),
): ArmoryLoadout[] {
    return loadouts.map((loadout) => {
        const sanitized = sanitizeArmoryLoadout(loadout);

        return sanitizeArmoryLoadout({
            ...sanitized,
            titan: resolveItemSelection('titans', sanitized.titan, armory, defaultLoadout.titan),
            kratosTable: resolveItemSelection('kratosTables', sanitized.kratosTable, armory, defaultLoadout.kratosTable),
            weapon: resolveItemSelection('weapons', sanitized.weapon, armory, defaultLoadout.weapon),
            gear: resolveItemSelections('gear', sanitized.gear ?? [], armory),
            mnemos: resolveItemSelections('mnemos', sanitized.mnemos ?? [], armory),
        });
    });
}

export function upsertCustomArmoryItem<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    items: T[],
    item: T,
    selectedItem?: T | null,
): T[] {
    const bundledItems = items.filter(isBundledArmoryItem) as T[];
    const customItems = items.filter((candidate) => !isBundledArmoryItem(candidate)) as T[];
    const preferredName = item.name.trim();
    const selectedCustom = selectedItem && !isBundledArmoryItem(selectedItem) ? selectedItem : null;
    const existingByName = customItems.find((candidate) => candidate.name === preferredName && candidate.id !== selectedCustom?.id);
    const preferredId = selectedCustom?.id ?? existingByName?.id;
    const nextCustomItem = prepareCustomArmoryItem(collectionKey, item, customItems, preferredId);
    const remainingItems = customItems.filter((candidate) => candidate.id !== nextCustomItem.id);

    return sortByName([...bundledItems, ...remainingItems, nextCustomItem]) as T[];
}

export function deleteCustomArmoryItem<T extends ArmoryItem>(items: T[], selectedItem?: T | null): T[] {
    if (!selectedItem || isBundledArmoryItem(selectedItem)) {
        return items;
    }

    return items.filter((item) => item.id !== selectedItem.id) as T[];
}

export function createCustomDuplicate<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    items: T[],
    item: T,
): T {
    const duplicateName = createUniqueCustomCopyName(item.name, items);
    return prepareCustomArmoryItem(collectionKey, { ...deepCopy(item), name: duplicateName }, items);
}

export function createUniqueCustomCopyName(name: string, items: ArmoryItem[]): string {
    const existingNames = new Set(items.map((item) => item.name));
    const baseName = `${name} (Custom)`;
    if (!existingNames.has(baseName)) {
        return baseName;
    }

    let counter = 2;
    while (existingNames.has(`${name} (Custom ${counter})`)) {
        counter += 1;
    }

    return `${name} (Custom ${counter})`;
}

export function isBundledArmoryItem(item?: ArmoryItem | null): boolean {
    return item?.source === 'bundled';
}

export function getBundledNameCollisions(
    collectionKey: ArmoryCollectionKey,
    uploadedItems: ArmoryItem[],
): string[] {
    const bundledNames = new Set(getCollectionByKey(BUNDLED_ARMORY, collectionKey).map((item) => item.name));
    return uploadedItems
        .map((item) => item.name?.trim())
        .filter((name): name is string => Boolean(name) && bundledNames.has(name))
        .sort((a, b) => a.localeCompare(b));
}

export function mergeUploadedArmoryItems<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    currentItems: T[],
    uploadedItems: T[],
): { items?: T[]; error?: string } {
    const normalizedUploads = normalizeStoredCustomCollection(collectionKey, uploadedItems) as T[];
    const collisions = getBundledNameCollisions(collectionKey, normalizedUploads);

    if (collisions.length > 0) {
        const label = getCollectionLabel(collectionKey);
        return {
            error: `Upload rejected. Bundled ${label} are read-only, so these names cannot be imported: ${collisions.join(', ')}.`,
        };
    }

    const bundledItems = currentItems.filter(isBundledArmoryItem) as T[];
    const customItems = currentItems.filter((item) => !isBundledArmoryItem(item)) as T[];
    const mergedCustomItems = [...customItems];

    normalizedUploads.forEach((upload) => {
        const existingMatch = mergedCustomItems.find((item) => item.name === upload.name);
        const nextItem = prepareCustomArmoryItem(collectionKey, upload, mergedCustomItems, existingMatch?.id);
        const existingIndex = mergedCustomItems.findIndex((item) => item.id === nextItem.id);

        if (existingIndex >= 0) {
            mergedCustomItems.splice(existingIndex, 1, nextItem as T);
        } else {
            mergedCustomItems.push(nextItem as T);
        }
    });

    return {
        items: sortByName([...bundledItems, ...mergedCustomItems]) as T[],
    };
}

export function previewSharedArmoryImport(
    recipe: SharedArmoryRecipe | undefined,
    armory: EffectiveArmoryData,
    fallbackTitle: string = 'Shared Loadout',
): SharedArmoryImportPreview | null {
    if (!recipe?.argonaut || recipe.bundledDatasetVersion !== BUNDLED_ARMORY_SHARE_CONTRACT_VERSION) {
        return null;
    }

    const requiredTags = new Set<string>();
    let nextArmory = deepCopy(armory);
    const titan = resolveSharedArmorySelection('titans', recipe.titan, nextArmory, requiredTags) as Titan | undefined;
    const kratosTable = resolveSharedArmorySelection('kratosTables', recipe.kratosTable, nextArmory, requiredTags) as KratosTable | undefined;
    const weapon = resolveSharedArmorySelection('weapons', recipe.weapon, nextArmory, requiredTags) as Weapon | undefined;
    const gear = resolveSharedArmorySelections('gear', recipe.gear, nextArmory, requiredTags) as Gear[];
    const mnemos = resolveSharedArmorySelections('mnemos', recipe.mnemos, nextArmory, requiredTags) as Mnemos[];

    if (!titan || !kratosTable || !weapon) {
        return null;
    }

    collectArgonautSpoilerTags(recipe.argonaut, requiredTags);

    return {
        armory: nextArmory,
        loadout: sanitizeArmoryLoadout({
            ...createDefaultArmoryLoadout(nextArmory),
            name: fallbackTitle.trim() || 'Shared Loadout',
            argonaut: deepCopy(recipe.argonaut),
            titan,
            kratosTable,
            weapon,
            gear,
            mnemos,
        }),
        requiredTags: Array.from(requiredTags).sort((a, b) => a.localeCompare(b)),
    };
}

function resolveSharedArmorySelection(
    collectionKey: ArmoryCollectionKey,
    selection: SharedArmoryItemSelection | undefined,
    armory: EffectiveArmoryData,
    requiredTags: Set<string>,
): ArmoryItem | undefined {
    if (!selection) {
        return undefined;
    }

    if (selection.kind === 'bundled-ref') {
        const match = findItemById(getCollectionByKey(armory, collectionKey), selection.id);
        if (!match || !isBundledArmoryItem(match)) {
            return undefined;
        }

        collectItemSpoilerTags(match, requiredTags);
        return match;
    }

    const imported = importSharedCustomArmoryItem(collectionKey, getCollectionByKey(armory, collectionKey), selection.item);
    armory[collectionKey] = imported.items as EffectiveArmoryData[typeof collectionKey];
    collectItemSpoilerTags(imported.item, requiredTags);
    return imported.item;
}

function resolveSharedArmorySelections(
    collectionKey: ArmoryCollectionKey,
    selections: SharedArmoryItemSelection[] | undefined,
    armory: EffectiveArmoryData,
    requiredTags: Set<string>,
): ArmoryItem[] {
    if (!Array.isArray(selections) || selections.length === 0) {
        return [];
    }

    return selections
        .map((selection) => resolveSharedArmorySelection(collectionKey, selection, armory, requiredTags))
        .filter(Boolean) as ArmoryItem[];
}

function importSharedCustomArmoryItem<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    items: T[],
    item: T,
): { items: T[]; item: T } {
    const normalized = normalizeArmoryItem(item, 'custom') as T;
    const bundledItems = items.filter(isBundledArmoryItem) as T[];
    const customItems = items.filter((candidate) => !isBundledArmoryItem(candidate)) as T[];
    const existingEquivalent = customItems.find((candidate) => {
        return (
            areArmoryItemsEquivalent(candidate, normalized) ||
            (candidate.bundledOriginId &&
                normalized.bundledOriginId &&
                candidate.bundledOriginId === normalized.bundledOriginId &&
                areArmoryItemsEquivalentIgnoringName(candidate, normalized))
        );
    });

    if (existingEquivalent) {
        return {
            items: sortByName([...bundledItems, ...customItems]) as T[],
            item: existingEquivalent,
        };
    }

    const importedItem = prepareCustomArmoryItem(collectionKey, normalized, customItems);
    return {
        items: sortByName([...bundledItems, ...customItems.filter((candidate) => candidate.id !== importedItem.id), importedItem]) as T[],
        item: importedItem,
    };
}

function collectItemSpoilerTags(item: ArmoryItem, requiredTags: Set<string>) {
    (item.tags ?? [])
        .filter(isRecognizedSpoilerTag)
        .forEach((tag) => requiredTags.add(tag));
}

function collectArgonautSpoilerTags(argonaut: ArmoryLoadout['argonaut'], requiredTags: Set<string>) {
    if ((argonaut.bleed ?? 0) > 0) {
        requiredTags.add(SpoilerTag.CYCLE_III);
    }

    if ((argonaut.midas ?? 0) > 0) {
        requiredTags.add(SpoilerTag.CYCLE_IV);
    }

    if ((argonaut.pain ?? 0) > 0) {
        requiredTags.add(SpoilerTag.CYCLE_V);
    }
}

function isRecognizedSpoilerTag(tag: string): boolean {
    return Object.values(SpoilerTag).includes(tag as SpoilerTag);
}

export function getDownloadableArmoryItems<T extends ArmoryItem>(collectionKey: ArmoryCollectionKey, items: T[]): T[] {
    return getCustomItemsForCollection(collectionKey, items);
}

export function migrateArmoryStorage(storage: StorageLike = localStorage): ArmoryMigrationResult {
    try {
        const overrides = loadArmoryOverrides(storage);
        const hasLegacyCollections = hasLegacyStoredCollections(storage);
        const storedLoadouts = parseStoredJson<Array<StoredArmoryLoadout | ArmoryLoadout>>(storage.getItem(LocalStorageKeys.LOADOUTS));
        const hasLegacyLoadouts = Array.isArray(storedLoadouts) && storedLoadouts.some((loadout) => !isStoredLoadoutReference(loadout));
        const needsMigration =
            storage.getItem(LocalStorageKeys.ARMORY_STORAGE_MODEL) !== ARMORY_STORAGE_MODEL_VERSION ||
            hasLegacyCollections ||
            hasLegacyLoadouts;

        if (!needsMigration) {
            storage.setItem(LocalStorageKeys.SCHEMA_VERSION, `${CURRENT_SCHEMA_VERSION}`);
            return {
                migrated: false,
                needsResetWarning: false,
                message: '',
            };
        }

        assertMigrationStorageIsParseable(storage);
        const baseOverrides = overrides ?? deriveLegacyOverrides(storage).overrides;
        const migratedLegacyLoadouts =
            Array.isArray(storedLoadouts) && !isStoredLoadoutReferenceArray(storedLoadouts)
                ? migrateLegacyArmoryLoadouts(storedLoadouts.map((loadout) => sanitizeArmoryLoadout(loadout)), baseOverrides)
                : { overrides: baseOverrides, loadouts: [] as ArmoryLoadout[] };
        const effectiveArmory = getEffectiveArmoryData(migratedLegacyLoadouts.overrides);
        const defaultLoadout = createDefaultArmoryLoadout(effectiveArmory);
        const migratedLoadouts = Array.isArray(storedLoadouts)
            ? isStoredLoadoutReferenceArray(storedLoadouts)
                ? storedLoadouts.map((loadout) => resolveStoredArmoryLoadout(loadout, effectiveArmory, defaultLoadout))
                : migratedLegacyLoadouts.loadouts
            : [defaultLoadout];

        saveArmoryOverrides(effectiveArmory, storage);
        saveStoredArmoryLoadouts(migratedLoadouts.filter(Boolean) as ArmoryLoadout[], effectiveArmory, storage);
        clearLegacyCollectionKeys(storage);
        storage.setItem(LocalStorageKeys.SCHEMA_VERSION, `${CURRENT_SCHEMA_VERSION}`);

        return {
            migrated: true,
            needsResetWarning: false,
            message: '',
        };
    } catch (error) {
        return {
            migrated: false,
            needsResetWarning: true,
            message: `Automatic armory migration failed: ${(error as Error).message}`,
        };
    }
}

function deriveLegacyOverrides(storage: StorageLike): { overrides: StoredArmoryOverrides } {
    const legacyData = readLegacyStoredArmoryData(storage);

    return {
        overrides: {
            titans: deriveCustomItemsFromLegacyCollection('titans', legacyData.titans, BUNDLED_ARMORY.titans),
            kratosTables: deriveCustomItemsFromLegacyCollection('kratosTables', legacyData.kratosTables, BUNDLED_ARMORY.kratosTables),
            weapons: deriveCustomItemsFromLegacyCollection('weapons', legacyData.weapons, BUNDLED_ARMORY.weapons),
            gear: deriveCustomItemsFromLegacyCollection('gear', legacyData.gear, BUNDLED_ARMORY.gear),
            mnemos: deriveCustomItemsFromLegacyCollection('mnemos', legacyData.mnemos, BUNDLED_ARMORY.mnemos),
        },
    };
}


function migrateLegacyArmoryLoadouts(
    legacyLoadouts: ArmoryLoadout[],
    overrides: StoredArmoryOverrides,
): { overrides: StoredArmoryOverrides; loadouts: ArmoryLoadout[] } {
    const nextOverrides: StoredArmoryOverrides = {
        titans: [...(overrides.titans ?? [])],
        kratosTables: [...(overrides.kratosTables ?? [])],
        weapons: [...(overrides.weapons ?? [])],
        gear: [...(overrides.gear ?? [])],
        mnemos: [...(overrides.mnemos ?? [])],
    };

    const migratedLoadouts = legacyLoadouts.map((loadout) => {
        const resolvedTitan = resolveLegacyLoadoutItem('titans', loadout.titan, nextOverrides) as Titan;
        const resolvedKratosTable = resolveLegacyLoadoutItem('kratosTables', loadout.kratosTable, nextOverrides) as KratosTable;
        const resolvedWeapon = resolveLegacyLoadoutItem('weapons', loadout.weapon, nextOverrides) as Weapon;
        const resolvedGear = (loadout.gear ?? []).map((item) => resolveLegacyLoadoutItem('gear', item, nextOverrides) as Gear);
        const resolvedMnemos = (loadout.mnemos ?? []).map((item) => resolveLegacyLoadoutItem('mnemos', item, nextOverrides) as Mnemos);
        const effectiveArmory = getEffectiveArmoryData(nextOverrides);
        const defaultLoadout = createDefaultArmoryLoadout(effectiveArmory);

        return sanitizeArmoryLoadout({
            ...loadout,
            titan: resolvedTitan ?? defaultLoadout.titan,
            kratosTable: resolvedKratosTable ?? defaultLoadout.kratosTable,
            weapon: resolvedWeapon ?? defaultLoadout.weapon,
            gear: resolvedGear.filter(Boolean),
            mnemos: resolvedMnemos.filter(Boolean),
        });
    });

    return {
        overrides: nextOverrides,
        loadouts: migratedLoadouts,
    };
}

function resolveLegacyLoadoutItem(
    collectionKey: ArmoryCollectionKey,
    item: ArmoryItem | undefined,
    overrides: StoredArmoryOverrides,
): ArmoryItem | undefined {
    if (!item) {
        return undefined;
    }

    const effectiveArmory = getEffectiveArmoryData(overrides);
    const collection = getCollectionByKey(effectiveArmory, collectionKey);
    const normalized = normalizeArmoryItem(item, 'custom');
    const bundledMatch = getCollectionByKey(BUNDLED_ARMORY, collectionKey).find((candidate) => candidate.name === normalized.name);

    if (bundledMatch) {
        if (areArmoryItemsEquivalent(normalized, bundledMatch)) {
            return bundledMatch;
        }

        const existingDuplicate = collection.find(
            (candidate) =>
                !isBundledArmoryItem(candidate) &&
                candidate.bundledOriginId === bundledMatch.id &&
                areArmoryItemsEquivalentIgnoringName(candidate, normalized),
        );
        if (existingDuplicate) {
            return existingDuplicate;
        }

        const duplicate = prepareCustomArmoryItem(
            collectionKey,
            {
                ...normalized,
                name: createUniqueCustomCopyName(bundledMatch.name, collection),
                bundledOriginId: bundledMatch.id,
                bundledOriginName: bundledMatch.name,
            },
            collection.filter((candidate) => !isBundledArmoryItem(candidate)),
        );
        appendCustomOverride(overrides, collectionKey, duplicate);
        return duplicate;
    }

    const existingCustom = collection.find(
        (candidate) =>
            !isBundledArmoryItem(candidate) &&
            (candidate.id === normalized.id || candidate.name === normalized.name || areArmoryItemsEquivalent(candidate, normalized)),
    );
    if (existingCustom) {
        return existingCustom;
    }

    const createdCustom = prepareCustomArmoryItem(
        collectionKey,
        normalized,
        collection.filter((candidate) => !isBundledArmoryItem(candidate)),
    );
    appendCustomOverride(overrides, collectionKey, createdCustom);
    return createdCustom;
}

function deriveOverridesFromEffectiveArmory(armory: EffectiveArmoryData): StoredArmoryOverrides {
    return {
        titans: getCustomItemsForCollection('titans', armory.titans),
        kratosTables: getCustomItemsForCollection('kratosTables', armory.kratosTables),
        weapons: getCustomItemsForCollection('weapons', armory.weapons),
        gear: getCustomItemsForCollection('gear', armory.gear),
        mnemos: getCustomItemsForCollection('mnemos', armory.mnemos),
    };
}

function getCustomItemsForCollection<T extends ArmoryItem>(collectionKey: ArmoryCollectionKey, items: T[]): T[] {
    const customItems = items.filter((item) => !isBundledArmoryItem(item));
    return normalizeStoredCustomCollection(collectionKey, customItems) as T[];
}

function deriveCustomItemsFromLegacyCollection<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    legacyItems: T[],
    bundledItems: T[],
): T[] {
    const bundledByName = new Map(bundledItems.map((item) => [item.name, item]));
    const customItems: T[] = [];

    legacyItems.forEach((item) => {
        const normalized = normalizeArmoryItem(item, 'custom') as T;
        const bundledMatch = bundledByName.get(normalized.name);

        if (bundledMatch && areArmoryItemsEquivalent(normalized, bundledMatch)) {
            return;
        }

        const candidate = bundledMatch
            ? ({
                  ...normalized,
                  name: createUniqueCustomCopyName(bundledMatch.name, [...bundledItems, ...customItems]),
                  bundledOriginId: bundledMatch.id,
                  bundledOriginName: bundledMatch.name,
              } as T)
            : normalized;

        customItems.push(prepareCustomArmoryItem(collectionKey, candidate, customItems));
    });

    return sortByName(customItems);
}

function attachBundledMetadata<T extends ArmoryItem>(items: T[], collectionKey: ArmoryCollectionKey): T[] {
    return sortByName(
        assignStableIds(
            items.map((item) => normalizeArmoryItem(item, 'bundled') as T),
            collectionKey,
            'bundled',
        ),
    );
}

function normalizeStoredCustomCollection<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    items?: T[] | null,
): T[] {
    if (!Array.isArray(items)) {
        return [];
    }

    return sortByName(
        assignStableIds(
            items.map((item) => normalizeArmoryItem(item, 'custom') as T),
            collectionKey,
            'custom',
        ),
    );
}

function mergeBundledAndCustomItems<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    bundledItems: T[],
    customItems: T[],
): T[] {
    const normalizedCustom = normalizeStoredCustomCollection(collectionKey, customItems) as T[];
    return sortByName([...bundledItems, ...normalizedCustom]) as T[];
}

function prepareCustomArmoryItem<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    item: T,
    existingItems: T[],
    preferredId?: string,
): T {
    const normalized = normalizeArmoryItem(item, 'custom') as T;
    const reusableId = normalized.id?.startsWith('custom:') ? normalized.id : undefined;
    const [prepared] = assignStableIds(
        [
            {
                ...normalized,
                id: preferredId ?? reusableId,
                source: 'custom',
            } as T,
        ],
        collectionKey,
        'custom',
        new Set(existingItems.filter((existing) => existing.id !== preferredId).map((existing) => existing.id).filter(Boolean) as string[]),
    );

    return prepared;
}

function assignStableIds<T extends ArmoryItem>(
    items: T[],
    collectionKey: ArmoryCollectionKey,
    source: ArmoryItemSource,
    existingIds: Set<string> = new Set(),
): T[] {
    const slugCounts = new Map<string, number>();

    return items.map((item) => {
        const baseSlug = slugify(item.name);
        const count = slugCounts.get(baseSlug) ?? 0;
        slugCounts.set(baseSlug, count + 1);

        const generatedId = `${source}:${collectionKey}:${baseSlug}${count > 0 ? `--${count + 1}` : ''}`;
        let nextId = item.id?.trim() || generatedId;

        if (existingIds.has(nextId)) {
            let suffix = count + 2;
            while (existingIds.has(`${generatedId}--${suffix}`)) {
                suffix += 1;
            }
            nextId = `${generatedId}--${suffix}`;
        }

        existingIds.add(nextId);

        return {
            ...item,
            id: nextId,
            source,
        };
    });
}

function annotateBundledSourceItems<T extends ArmoryItem>(
    items: T[],
    collectionKey: ArmoryCollectionKey,
    sourceLabel: 'base' | 'atcc',
): T[] {
    // Bundled ids participate in shared loadout links, so they are a public contract.
    // Preserve existing ids when present and treat changes to this format as intentional share-version work.
    return items.map((item, index) => ({
        ...item,
        id: item.id?.trim() || `bundled:${collectionKey}:${sourceLabel}:${index + 1}`,
    }));
}

function normalizeArmoryItem<T extends ArmoryItem>(item: T, source: ArmoryItemSource): T {
    const normalized = normalizeATCCItem({
        ...item,
        tags: item.tags ?? [],
        stats: deepCopy(item.stats ?? []),
    } as T);

    return {
        ...normalized,
        source,
    };
}

function areArmoryItemsEquivalent(a: ArmoryItem, b: ArmoryItem): boolean {
    return JSON.stringify(toComparableArmoryItem(a)) === JSON.stringify(toComparableArmoryItem(b));
}

function areArmoryItemsEquivalentIgnoringName(a: ArmoryItem, b: ArmoryItem): boolean {
    return JSON.stringify(toComparableArmoryItem(a, true)) === JSON.stringify(toComparableArmoryItem(b, true));
}

function toComparableArmoryItem(item: ArmoryItem, ignoreName: boolean = false) {
    const normalized = normalizeArmoryItem(item, item.source ?? 'custom');
    return {
        name: ignoreName ? null : normalized.name,
        tags: [...(normalized.tags ?? [])].sort(),
        stats: normalized.stats,
        bundledOriginId: normalized.bundledOriginId ?? null,
        bundledOriginName: normalized.bundledOriginName ?? null,
    };
}

function serializeArmoryLoadout(
    loadout: ArmoryLoadout,
    armory: EffectiveArmoryData,
    defaultLoadout: ArmoryLoadout,
): StoredArmoryLoadout {
    const sanitized = sanitizeArmoryLoadout(loadout);

    return {
        name: sanitized.name,
        argonaut: sanitized.argonaut,
        titanId: resolveStoredItemId('titans', sanitized.titan, armory, defaultLoadout.titan.id!),
        kratosTableId: resolveStoredItemId('kratosTables', sanitized.kratosTable, armory, defaultLoadout.kratosTable.id!),
        weaponId: resolveStoredItemId('weapons', sanitized.weapon, armory, defaultLoadout.weapon.id!),
        gearIds: resolveStoredItemIds('gear', sanitized.gear ?? [], armory),
        mnemosIds: resolveStoredItemIds('mnemos', sanitized.mnemos ?? [], armory),
    };
}

function resolveStoredArmoryLoadout(
    storedLoadout: StoredArmoryLoadout,
    armory: EffectiveArmoryData,
    defaultLoadout: ArmoryLoadout,
): ArmoryLoadout {
    return sanitizeArmoryLoadout({
        name: storedLoadout.name,
        argonaut: storedLoadout.argonaut,
        titan: findItemById(armory.titans, storedLoadout.titanId) ?? defaultLoadout.titan,
        kratosTable: findItemById(armory.kratosTables, storedLoadout.kratosTableId) ?? defaultLoadout.kratosTable,
        weapon: findItemById(armory.weapons, storedLoadout.weaponId) ?? defaultLoadout.weapon,
        gear: storedLoadout.gearIds.map((itemId) => findItemById(armory.gear, itemId)).filter(Boolean) as Gear[],
        mnemos: storedLoadout.mnemosIds.map((itemId) => findItemById(armory.mnemos, itemId)).filter(Boolean) as Mnemos[],
    });
}

function resolveStoredItemId(
    collectionKey: ArmoryCollectionKey,
    item: ArmoryItem,
    armory: EffectiveArmoryData,
    fallbackId: string,
): string {
    const collection = getCollectionByKey(armory, collectionKey);
    return findMatchingArmoryItem(collection, item)?.id ?? fallbackId;
}

function resolveStoredItemIds(
    collectionKey: ArmoryCollectionKey,
    items: ArmoryItem[],
    armory: EffectiveArmoryData,
): string[] {
    const collection = getCollectionByKey(armory, collectionKey);
    const resolvedIds = items.map((item) => findMatchingArmoryItem(collection, item)?.id).filter(Boolean) as string[];
    return Array.from(new Set(resolvedIds));
}

function resolveItemSelection<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    item: T,
    armory: EffectiveArmoryData,
    fallbackItem: T,
): T {
    const collection = getCollectionByKey(armory, collectionKey) as T[];
    return (findMatchingArmoryItem(collection, item) as T | undefined) ?? fallbackItem;
}

function resolveItemSelections<T extends ArmoryItem>(
    collectionKey: ArmoryCollectionKey,
    items: T[],
    armory: EffectiveArmoryData,
): T[] {
    const collection = getCollectionByKey(armory, collectionKey) as T[];
    const resolved = items.map((item) => findMatchingArmoryItem(collection, item) as T | undefined).filter(Boolean) as T[];
    return Array.from(new Map(resolved.map((item) => [item.id ?? item.name, item])).values());
}

function findMatchingArmoryItem<T extends ArmoryItem>(collection: T[], candidate?: T | null): T | undefined {
    if (!candidate) {
        return undefined;
    }

    return collection.find((item) => item.id === candidate.id) ?? collection.find((item) => item.name === candidate.name);
}

function findItemById<T extends ArmoryItem>(collection: T[], itemId?: string): T | undefined {
    return collection.find((item) => item.id === itemId);
}

function readLegacyStoredArmoryData(storage: StorageLike): LegacyStoredArmoryData {
    return {
        titans: normalizeLegacyCollection(storage.getItem(LocalStorageKeys.TITANS)),
        kratosTables: normalizeLegacyCollection(storage.getItem(LocalStorageKeys.KRATOS_TABLES)),
        weapons: normalizeLegacyCollection(storage.getItem(LocalStorageKeys.WEAPONS)),
        gear: normalizeLegacyCollection(storage.getItem(LocalStorageKeys.GEAR)),
        mnemos: normalizeLegacyCollection(storage.getItem(LocalStorageKeys.MNEMOS)),
    };
}

function normalizeLegacyCollection<T extends ArmoryItem>(rawValue: string | null): T[] {
    const parsed = parseStoredJson<T[]>(rawValue);
    return Array.isArray(parsed) ? parsed.map((item) => normalizeArmoryItem(item, 'custom') as T) : [];
}

function hasLegacyStoredCollections(storage: StorageLike): boolean {
    return [LocalStorageKeys.TITANS, LocalStorageKeys.KRATOS_TABLES, LocalStorageKeys.WEAPONS, LocalStorageKeys.GEAR, LocalStorageKeys.MNEMOS].some(
        (key) => Array.isArray(parseStoredJson(storage.getItem(key))),
    );
}

function clearLegacyCollectionKeys(storage: StorageLike) {
    storage.removeItem(LocalStorageKeys.TITANS);
    storage.removeItem(LocalStorageKeys.KRATOS_TABLES);
    storage.removeItem(LocalStorageKeys.WEAPONS);
    storage.removeItem(LocalStorageKeys.GEAR);
    storage.removeItem(LocalStorageKeys.MNEMOS);
}

function appendCustomOverride(
    overrides: StoredArmoryOverrides,
    collectionKey: ArmoryCollectionKey,
    item: ArmoryItem,
) {
    const nextCollection = [...(overrides[collectionKey] ?? [])].filter((candidate) => candidate.id !== item.id);
    nextCollection.push(item);
    overrides[collectionKey] = sortByName(nextCollection as ArmoryItem[]) as StoredArmoryOverrides[typeof collectionKey];
}

function getCollectionByKey(armory: EffectiveArmoryData, collectionKey: ArmoryCollectionKey) {
    return armory[collectionKey];
}

function getCollectionLabel(collectionKey: ArmoryCollectionKey): string {
    switch (collectionKey) {
        case 'kratosTables':
            return 'Kratos Tables';
        case 'gear':
            return 'Gear';
        case 'mnemos':
            return 'Mnemos';
        case 'titans':
            return 'Titans';
        case 'weapons':
        default:
            return 'Weapons';
    }
}

function slugify(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80) || 'item';
}

function sortByName<T extends ArmoryItem>(items: T[]): T[] {
    return items.slice().sort((a, b) => a.name.localeCompare(b.name));
}

function parseStoredJson<T>(value: string | null): T | null {
    if (!value) {
        return null;
    }

    return tryParseStoredJson<T>(value).value;
}

function isStoredLoadoutReference(loadout: StoredArmoryLoadout | ArmoryLoadout): loadout is StoredArmoryLoadout {
    return typeof (loadout as StoredArmoryLoadout).titanId === 'string';
}

function isStoredLoadoutReferenceArray(loadouts: Array<StoredArmoryLoadout | ArmoryLoadout>): loadouts is StoredArmoryLoadout[] {
    return loadouts.every(isStoredLoadoutReference);
}

function assertMigrationStorageIsParseable(storage: StorageLike) {
    const keysToValidate = [
        LocalStorageKeys.ARMORY_OVERRIDES,
        LocalStorageKeys.TITANS,
        LocalStorageKeys.KRATOS_TABLES,
        LocalStorageKeys.WEAPONS,
        LocalStorageKeys.GEAR,
        LocalStorageKeys.MNEMOS,
        LocalStorageKeys.LOADOUTS,
    ];

    keysToValidate.forEach((key) => {
        const rawValue = storage.getItem(key);
        if (!rawValue) {
            return;
        }

        const parsed = tryParseStoredJson(rawValue);
        if (parsed.error) {
            throw new Error(`stored ${key} is not valid JSON`);
        }
    });
}

function tryParseStoredJson<T>(value: string): { value: T | null; error: boolean } {
    try {
        return {
            value: JSON.parse(value) as T,
            error: false,
        };
    } catch {
        return {
            value: null,
            error: true,
        };
    }
}
